'use client'

import { DictionaryContext } from '../../providers'
import { resolveFormStepData } from './helpers/resolveFormStepData'
import {
    ACTION_FAILED_EVENT,
    ACTION_SUCCESS_EVENT,
    SUBMIT_FAILED_EVENT,
    SUBMIT_SUCCESS_EVENT,
    ActionFailedEventDetail,
    SubmitFailedEventDetail,
    SubmitSuccessEventDetail,
    sendAnalyticsEvent,
} from '../../../../MyStichtingOpenHelpers/src/static'
import { useContext, useEffect, useRef } from 'react'
import {
    mapToFormStartEvent,
    mapSubmitSuccessToFormCompleteEvent,
    mapActionFailedToFormErrorEvent,
    mapSubmitFailedToFormErrorEvent,
    mapToFormInteractionEvent,
    mapToFormProgressEvent,
} from './helpers/mappers'
import { DO_NOT_TRACK_ATTRIBUTE_NAME, DO_NOT_TRACK_SELECTOR, SEARCH_ANALYTICS_INPUT_NAME } from './constants'

const isDoNotTrack = (target: HTMLElement) => {
    return target.hasAttribute(DO_NOT_TRACK_ATTRIBUTE_NAME) || !!target.closest(DO_NOT_TRACK_SELECTOR)
}

interface Props {
    /** The key of the page that the form is on. Is used to reset the form tracking state when the page changes. */
    pageKey: string
}
/**
 * Subscribes to global form-related events and sends GA4 analytics events.
 *
 * Behavior:
 * - Tracks state per `formId` so multiple forms on the same page do not share `form_start` or submit attempt counts.
 * - Sends `form_start` on the first tracked interaction for each form.
 * - Sends `form_interaction` on each `change` event for supported form controls.
 * - Increments submit attempts per form for success and failure submit/action events.
 * - Resets all in-memory tracking state whenever `pageKey` changes.
 *
 * Privacy:
 * - Fields marked with `data-no-analytics` (or nested inside such an element)
 *   are excluded from value tracking.
 */
type FormTrackingState = {
    submitAttempts: number
    hasStarted: boolean
    lastProgressIndex: string | null
}

export const useFormAnalytics = ({ pageKey }: Props) => {
    const formStateByIdRef = useRef<Record<string, FormTrackingState>>({})
    const dictionary = useContext(DictionaryContext)

    useEffect(() => {
        formStateByIdRef.current = {}
    }, [pageKey])

    const getOrCreateFormTrackingState = (formId: string) => {
        const state = formStateByIdRef.current[formId]
        if (state) return state

        const initialState: FormTrackingState = {
            submitAttempts: 0,
            hasStarted: false,
            lastProgressIndex: null,
        }
        formStateByIdRef.current[formId] = initialState
        return initialState
    }

    /**On first interaction with a form field (on blur) send this event */
    const sendStartEvent = (form: HTMLFormElement) => {
        const formId = form.getAttribute('id') ?? ''
        const formState = getOrCreateFormTrackingState(formId)
        sendAnalyticsEvent(mapToFormStartEvent(formId))
        formState.hasStarted = true
    }

    /** On load of a new form step in a multistep form, unless the form has been completed (form_complete) send this event */
    const sendProgressEvent = (formId: string) => {
        const formState = getOrCreateFormTrackingState(formId)
        const { form_name, tm_index, tm_name } = resolveFormStepData(formId)
        if (formState.lastProgressIndex === tm_index) {
            return
        }
        const isDefaultSingleStepShape = form_name === formId && tm_name === formId && tm_index === '1'
        if (isDefaultSingleStepShape) {
            return
        }

        formState.submitAttempts += 1
        formState.lastProgressIndex = tm_index
        sendAnalyticsEvent(mapToFormProgressEvent(formId, formId, formState.submitAttempts))
    }

    const sendProgressEventOnSubmitSuccess = (e: Event) => {
        const customEvent = e as CustomEvent<SubmitSuccessEventDetail>
        sendProgressEvent(customEvent.detail.formId)
    }

    /**
     * On successfully submitting the final form step (if applicable; on load of the success page) send this event
     */
    const sendCompleteEvent = (e: Event) => {
        const customEvent = e as CustomEvent<SubmitSuccessEventDetail>
        const formState = getOrCreateFormTrackingState(customEvent.detail.formId)
        formState.submitAttempts += 1
        sendAnalyticsEvent(mapSubmitSuccessToFormCompleteEvent(customEvent.detail, formState.submitAttempts))
    }

    /**On NOT successfully submitting a form step send this event */
    const sendActionFailedFormErrorEvent = (e: Event) => {
        const customEvent = e as CustomEvent<ActionFailedEventDetail>
        const formState = getOrCreateFormTrackingState(customEvent.detail.formId)
        formState.submitAttempts += 1
        sendAnalyticsEvent(
            mapActionFailedToFormErrorEvent(customEvent.detail, formState.submitAttempts, dictionary?.getText),
        )
    }

    const sendSubmitFailedFormErrorEvent = (e: Event) => {
        const customEvent = e as CustomEvent<SubmitFailedEventDetail>
        const formState = getOrCreateFormTrackingState(customEvent.detail.formId)
        formState.submitAttempts += 1
        sendAnalyticsEvent(
            mapSubmitFailedToFormErrorEvent(customEvent.detail, formState.submitAttempts, dictionary?.getText),
        )
    }

    /**On interacting with a form field that has not been disabled for interaction tracking (in case of PII) send this event */
    const sendFormInteractionEvent = (e: Event) => {
        const target = e.target as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null
        if (!target) return
        if (target.name === SEARCH_ANALYTICS_INPUT_NAME) return

        const form = target.form
        if (form) {
            const formId = form.getAttribute('id') ?? ''
            const formState = getOrCreateFormTrackingState(formId)

            if (!formState.hasStarted) {
                sendStartEvent(form)
            }

            const fieldName = target.name || target.id || ''
            const value = isDoNotTrack(target) ? '' : target.value

            sendAnalyticsEvent(mapToFormInteractionEvent(formId, fieldName, value))
            return
        }
    }

    useEffect(() => {
        const abortController = new AbortController()
        const { signal } = abortController
        document.addEventListener(ACTION_FAILED_EVENT, sendActionFailedFormErrorEvent, { signal })
        document.addEventListener(SUBMIT_FAILED_EVENT, sendSubmitFailedFormErrorEvent, { signal })
        document.addEventListener(SUBMIT_SUCCESS_EVENT, sendProgressEventOnSubmitSuccess, { signal })
        document.addEventListener(ACTION_SUCCESS_EVENT, sendCompleteEvent, { signal })
        document.addEventListener('change', sendFormInteractionEvent, { signal })

        return () => {
            abortController.abort()
        }
    }, [])

    return {}
}
